'use client';

import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { css } from '@bts-web/utils-style-engine';
import {
  PrecisionFloat,
  TimeScalar,
  timeScalarToPlainString,
} from '@bts-web/utils-formatting';
import { getAppConfig } from '@bts-web/core-app-config';
import { useFeatureFlag } from '../../statsig/sdk/provider';
import { useClientLocale } from '@bts-web/utils-lokalise';
import { TradeAssetInfoDisplay } from '../../common/components/TradeAssetInfoDisplay/TradeAssetInfoDisplay';
import { OfferLoaderElement } from '../Reusable/OfferLoaderElement/OfferLoaderElement';
import { FeesList } from '../Reusable/FeesList/FeesList';
import { SwappedAssetsSummaryBox } from './SwappedAssetsSummaryBox';
import { getFeesListItemsForSwap } from './getFeesListItemsForSwap';
import { TradeTaxSummary } from '../Reusable/TradeTaxSummary/TradeTaxSummary';
import { AcceptSwapScreenTranslations } from './getAcceptSwapTranslations';
import { TransactionStatus } from '@bts-web/data-layer/server';
import { getOfferAvailabilityInMs } from '../utils/getOfferAvailabilityInMs';
import { createSwapOffer } from './server-functions/createSwapOffer';
import { acceptSwapOffer } from './server-functions/acceptSwapOffer';
import { AcceptBuyTradeDisclaimerWrapper } from '../AcceptTrade/AcceptTradeSubscriptionWrapper/components/AcceptBuyTradeDisclaimerWrapper/AcceptBuyTradeDisclaimerWrapper';
import { AssetType } from '../utils';
import { FinishTradeStatusModal } from '../FinishTrade/FinishTradeStatusModal/FinishTradeStatusModal';
import {
  ButtonBase,
  HyperLinkButtonBase,
  useHandleGqlErrorsWithSnackbar,
} from '../../common';
import { useModalDrawerContext } from '../../common/components/ModalDrawerContext/ModalDrawerContext';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { FilledSpinnerIos } from '../../common/svgIcons';
import { MutableNonNullableAsset } from '../CreateTrade/types';

export const AcceptSwap = ({
  initialOfferData,
  amountFor,
  translations,
}: {
  initialOfferData: NonNullable<
    Awaited<ReturnType<typeof createSwapOffer>>['data']
  >;
  amountFor: 'SOURCE' | 'TARGET';
  translations: AcceptSwapScreenTranslations;
}) => {
  const [swapSuccess, setSwapSuccess] = useState(false);

  const [swapOfferData, setSwapOfferData] = useState(initialOfferData);

  const [nextExpiresAt, setNextExpiresAt] = useState<TimeScalar | null>(
    initialOfferData.swapOffer.expiresAt ?? null,
  );

  const [isAnyRequestOngoing, setIsAnyRequestOngoing] = useState(false);

  const [isOfferAccepted, setIsOfferAccepted] = useState(false);

  const { showModal } = useModalDrawerContext();

  const { currency } = getAppConfig();

  const feature_areTaxDetailsShownOnOfferAccept = useFeatureFlag(
    'feature_areTaxDetailsShownOnOfferAccept',
  );

  const currentLocale = useClientLocale();

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const showFinishTradeModal = (
    status: TransactionStatus,
    fiatAmount: PrecisionFloat,
    taxAmount: PrecisionFloat,
  ) => {
    showModal(FinishTradeStatusModal, {
      transactionData: {
        __typename: initialOfferData.swapOffer.__typename,
        asset: initialOfferData.swapOffer.targetAsset as ComponentProps<
          typeof FinishTradeStatusModal
        >['transactionData']['asset'],
        taxAmount,
        status,
        fiatAmount,
        assetAmount: initialOfferData.swapOffer.targetAmount,
        sourceAmount: initialOfferData.swapOffer.sourceAmount,
        sourceAsset: initialOfferData.swapOffer.sourceAsset as ComponentProps<
          typeof FinishTradeStatusModal
        >['transactionData']['sourceAsset'],
      },
      hasTaxOfflineWarning: false,
    });
  };

  const stopOfferRefetching = useCallback(() => {
    setNextExpiresAt(null);

    setIsAnyRequestOngoing(false);
  }, []);

  const refreshSwapOffer = useCallback(async () => {
    if (isAnyRequestOngoing || isOfferAccepted) return;

    setIsAnyRequestOngoing(true);

    const { errors, data } = await createSwapOffer({
      sourceAssetId: initialOfferData.swapOffer.sourceAsset.id,
      targetAssetId: initialOfferData.swapOffer.targetAsset.id,
      amount:
        amountFor === 'SOURCE'
          ? (
              initialOfferData.swapOffer.sourceAmount as {
                value: string;
                precision: number;
              }
            ).value
          : (
              initialOfferData.swapOffer.targetAmount as {
                value: string;
                precision: number;
              }
            ).value,
      amountDefinedFor: amountFor,
    });

    if (errors || !data) {
      processErrors(errors);

      stopOfferRefetching();

      return;
    }

    setSwapOfferData(data);

    setNextExpiresAt(data?.swapOffer.expiresAt ?? null);

    document.dispatchEvent(new Event('onTradeOfferRefetched'));

    setIsAnyRequestOngoing(false);
  }, [isAnyRequestOngoing, isOfferAccepted, amountFor, initialOfferData]);

  const offerAvailabilityInMs = useMemo(
    () => getOfferAvailabilityInMs(nextExpiresAt),
    [nextExpiresAt],
  );

  const triggerAcceptSwapOffer = async () => {
    setIsAnyRequestOngoing(true);

    setIsOfferAccepted(true);

    try {
      const { errors, data } = await acceptSwapOffer(
        swapOfferData.swapOffer.id,
      );

      if (errors) {
        processErrors(errors);

        if (
          errors.includes('offer.not.latest') ||
          errors.includes('swap.offer.not.latest')
        ) {
          setIsOfferAccepted(false);

          await refreshSwapOffer();
        } else {
          setIsOfferAccepted(false);

          stopOfferRefetching();
        }

        return;
      }

      const { status, fiatAmount, taxAmount } = data;

      showFinishTradeModal(status as TransactionStatus, fiatAmount, taxAmount);

      setSwapSuccess(true);
    } catch (error) {
      console.error('Error accepting swap offer:', error);

      setIsOfferAccepted(false);

      if (typeof error === 'string') {
        processErrors([error]);
      } else {
        processErrors(error as string[]);
      }

      stopOfferRefetching();
    }
  };

  const loaderElement = useMemo(() => {
    if (isOfferAccepted || !nextExpiresAt) {
      return null;
    }

    return (
      <OfferLoaderElement
        key={timeScalarToPlainString(nextExpiresAt)}
        duration={offerAvailabilityInMs}
        onComplete={refreshSwapOffer}
        onRefocus={refreshSwapOffer}
        beforeCompleteCallbackMs={1000}
        beforeCompleteCallback={() => setIsAnyRequestOngoing(true)}
      />
    );
  }, [nextExpiresAt, offerAvailabilityInMs, isOfferAccepted]);

  useEffect(() => {
    const handleWarningModalContinue = () => stopOfferRefetching();

    document.addEventListener(
      'onWarningModalContinue',
      handleWarningModalContinue,
    );

    return () => {
      document.removeEventListener(
        'onWarningModalContinue',
        handleWarningModalContinue,
      );
    };
  }, []);

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        px: 'medium',
        height: '100%',
        gap: 'extra_small',
      })}
    >
      <TradeAssetInfoDisplay
        sourceAssetData={
          swapOfferData.swapOffer.sourceAsset as MutableNonNullableAsset
        }
        targetAssetData={
          swapOfferData.swapOffer.targetAsset as MutableNonNullableAsset
        }
        swapExchangeRate={swapOfferData.exchangeRate}
        operation="swap"
        hidePrimaryAssetLogo
        loaderElement={loaderElement}
      />

      <SwappedAssetsSummaryBox
        translations={translations}
        offerData={swapOfferData.swapOffer}
        locale={currentLocale}
        currency={currency}
      />

      <div>
        <FeesList
          showPriceGuarantee={translations.priceGuarantee}
          headerRightSlotContent={translations.feesIncluded}
          offerFeesDescription={translations.offerFeesDescription}
          items={getFeesListItemsForSwap(
            swapOfferData.swapOffer,
            {
              swapAmount: translations.swapAmount,
              swapVolume: translations.swapVolume,
              tradeFee: translations.tradeFee,
              tradeFeeInclVAT: translations.tradeFeeInclVAT,
              offerPrice: translations.offerPrice,
            },
            currentLocale,
            currency,
          )}
        />

        {feature_areTaxDetailsShownOnOfferAccept &&
          swapOfferData.swapOffer.taxAmount &&
          parseFloat(swapOfferData.swapOffer.taxAmount.value) > 0 && (
            <TradeTaxSummary
              currency={currency}
              currentLocale={currentLocale}
              receivedAmount={swapOfferData.swapOffer.targetAmount}
              receivedAmountDisplayType="units"
              symbol={swapOfferData.swapOffer.targetAsset.symbol as string}
              isTaxServiceOffline={false}
              taxAmount={swapOfferData.swapOffer.taxAmount}
              translations={translations}
            />
          )}
      </div>

      <div
        className={css({
          marginTop: 'auto',
          paddingBottom: 'medium',
        })}
      >
        {swapSuccess ? (
          <HyperLinkButtonBase to={NEXT_ROUTES.PORTFOLIO} visual="primary">
            {translations.goToPortfolio}
          </HyperLinkButtonBase>
        ) : (
          <AcceptBuyTradeDisclaimerWrapper
            translations={translations}
            variant="swap"
            legalUrl={swapOfferData.swapOffer.targetAsset.legalUrl as string}
            assetType={
              swapOfferData.swapOffer.targetAsset.__typename as AssetType
            }
          >
            {({ areTermsAgreedTo }) => (
              <div className={css({ paddingTop: 'extra_large_2' })}>
                <ButtonBase
                  visual="primary"
                  size="large"
                  disabled={!areTermsAgreedTo || isAnyRequestOngoing}
                  onClick={triggerAcceptSwapOffer}
                  fullWidth
                >
                  {translations.swapNow}

                  {isAnyRequestOngoing && (
                    <FilledSpinnerIos
                      aria-label="loading icon"
                      data-element="loading-icon"
                    />
                  )}
                </ButtonBase>
              </div>
            )}
          </AcceptBuyTradeDisclaimerWrapper>
        )}
      </div>
    </div>
  );
};
